import type { ObjectArchetype, ObjectEffects } from "@mind-sands/shared";

/**
 * Read-only summary of an object's compiled effects: archetype badge,
 * particle emitters, and attached lights.
 */
export function ObjectEffectsSummary({
  archetype,
  effects,
}: {
  archetype?: ObjectArchetype;
  effects?: ObjectEffects;
}) {
  if (!archetype && !effects) return null;

  return (
    <div className="mb-3 pb-2 border-b border-sands-100">
      <p className="text-[10px] font-medium text-sands-500 uppercase tracking-wider mb-1.5">
        Effects
      </p>
      {archetype && (
        <div className="flex items-center gap-2 text-xs mb-2">
          <span className="text-sands-400 w-16 shrink-0">Archetype</span>
          <span className="px-1.5 py-0.5 bg-healing-50 text-healing-700 border border-healing-200 rounded text-[10px] font-medium">
            {archetype}
          </span>
        </div>
      )}
      {effects?.emitters && effects.emitters.length > 0 && (
        <div className="mb-2">
          <p className="text-[10px] text-sands-400 mb-1">Emitters</p>
          {effects.emitters.map((emitter, i) => (
            <div key={i} className="flex items-center gap-2 text-xs mb-1">
              <span className="px-1.5 py-0.5 bg-orange-50 text-orange-700 border border-orange-200 rounded text-[10px] font-medium">
                {emitter.type}
              </span>
              {emitter.params && (
                <span className="text-sands-400 text-[10px]">
                  {"count" in emitter.params &&
                    `count: ${(emitter.params as { count?: number }).count}`}
                  {"radius" in emitter.params &&
                    ` · r: ${(emitter.params as { radius?: number }).radius}`}
                  {"height" in emitter.params &&
                    ` · h: ${(emitter.params as { height?: number }).height}`}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      {effects?.lights && effects.lights.length > 0 && (
        <div>
          <p className="text-[10px] text-sands-400 mb-1">Lights</p>
          {effects.lights.map((light, i) => (
            <div key={i} className="flex items-center gap-2 text-xs mb-1">
              <span className="px-1.5 py-0.5 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded text-[10px] font-medium">
                {light.type}
              </span>
              <span className="text-sands-400 text-[10px]">
                {light.color && (
                  <span className="inline-flex items-center gap-1">
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full border border-sands-200"
                      style={{ backgroundColor: light.color }}
                    />
                    {light.color}
                  </span>
                )}
                {light.intensity !== undefined && ` · i: ${light.intensity}`}
                {light.distance !== undefined && ` · d: ${light.distance}`}
                {light.flicker && " · flicker"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
